import type { ConsentTypeToFetch, VerticalConsents } from '@nimbus/types'
import { EMAIL_AND_PHONE_CONSENT_CHECKBOX, EMAIL_CONSENT_CHECKBOX, PHONE_CONSENT_CHECKBOX } from '@nimbus/constants'
import { type ConsentRequestBrand, type ConsentComponents, ConsentRequestType, ConsentRequestVerification_status, type UseQueryFunctionOptions, type ConsentPaths } from '@core/types'
import { computed, useRoute, useQuery, useConsentClient, useCoreConfig, useRecurringConsentCode, useState } from '#imports'

// Export types for reuse
export type GetConsentOptions = Omit<UseQueryFunctionOptions<ConsentPaths['/public/consent']['get']['responses']['200']['content']['application/json'] | undefined>, 'key'>
export type GetRecurringConsentOptions = Omit<UseQueryFunctionOptions<ConsentPaths['/public/consent/recurring']['get']['responses']['200']['content']['application/json'] | undefined>, 'key'>
export type GetConsentData = ConsentPaths['/public/consent']['get']['responses']['200']['content']['application/json']
export type RecurringConsentData = ConsentPaths['/public/consent/recurring']['get']['responses']['200']['content']['application/json']

export const useRecurringConsent = () => {
    const { site } = useCoreConfig()
    const route = useRoute()
    const { client: consentClient } = useConsentClient()
    const { consentCode } = useRecurringConsentCode()

    // No initialiser: `undefined` until first fetch, so consumers can reset it without casting.
    const consentStatuses = useState<RecurringConsentData | undefined>('consent:recurring:consentStatuses')
    const hasSubmittedConsent = useState('consent:recurring:hasSubmittedConsent', () => false)

    const hasFetchedConsents = computed(() => consentStatuses.value !== undefined)

    const isReady = computed(() => hasFetchedConsents.value)

    const missingEmailConsent = computed(() => consentStatuses.value?.some(consent => consent.type === ConsentRequestType.Value3 && !isConsentAccepted(consent)))

    const missingPhoneConsent = computed(() => consentStatuses.value?.some(consent => consent.type === ConsentRequestType.Value4 && !isConsentAccepted(consent)))

    const missingBingoEmailConsent = computed(() => consentStatuses.value?.some(consent => consent.type === ConsentRequestType.Value6 && !isConsentAccepted(consent)))

    const missingCasinoEmailConsent = computed(() => consentStatuses.value?.some(consent => consent.type === ConsentRequestType.Value7 && !isConsentAccepted(consent)))

    const missingBingoPhoneConsent = computed(() => consentStatuses.value?.some(consent => consent.type === ConsentRequestType.Value8 && !isConsentAccepted(consent)))

    const missingCasinoPhoneConsent = computed(() => consentStatuses.value?.some(consent => consent.type === ConsentRequestType.Value9 && !isConsentAccepted(consent)))

    const isBothConsentsMissing = computed(() => missingEmailConsent.value && missingPhoneConsent.value)

    const isAnyConsentMissing = computed(() => missingEmailConsent.value || missingPhoneConsent.value)

    const isAnyVerticalConsentMissing = computed(() => missingBingoEmailConsent.value || missingCasinoEmailConsent.value || missingBingoPhoneConsent.value || missingCasinoPhoneConsent.value)

    const forceShow = computed(() => consentCode.value || route.meta?.forceRecurringConsent)

    const showModal = computed(() => isAnyConsentMissing.value)

    const consentTypeToFetch = computed<ConsentTypeToFetch | undefined>(() => {
        if (!isReady.value) return
        if (isBothConsentsMissing.value) return EMAIL_AND_PHONE_CONSENT_CHECKBOX
        if (missingEmailConsent.value) return EMAIL_CONSENT_CHECKBOX
        if (missingPhoneConsent.value) return PHONE_CONSENT_CHECKBOX

        return
    })

    function isConsentAccepted(consent: ConsentComponents['schemas']['PlayerConsent']) {
        return consent.verification_status === ConsentRequestVerification_status.ACCEPTED
    }

    function getVerticalConsentType(type: keyof VerticalConsents) {
        switch (type) {
            case 'bingoEmail':
                return ConsentRequestType.Value6
            case 'casinoEmail':
                return ConsentRequestType.Value7
            case 'bingoPhone':
                return ConsentRequestType.Value8
            case 'casinoPhone':
                return ConsentRequestType.Value9
        }
    }

    function getVerticalConsentKey(type: ConsentRequestType) {
        switch (type) {
            case ConsentRequestType.Value6:
                return 'bingoEmail'
            case ConsentRequestType.Value7:
                return 'casinoEmail'
            case ConsentRequestType.Value8:
                return 'bingoPhone'
            case ConsentRequestType.Value9:
                return 'casinoPhone'
        }
    }

    function mergeTextWithSpace(array: []): string {
        return array.map((item: any) => item.type === 'text' ? item.text : mergeTextWithSpace(item.children)).join('')
    }

    function getMergedConsentTexts(content: []): string {
        if (!content?.length) return ''

        return mergeTextWithSpace(content)
    }

    function getConsent(options: GetConsentOptions = {}) {
        return useQuery(async ({ signal }) => {
            const { data } = await consentClient.GET('/public/consent', { signal })

            return data
        }, {
            ...options,
            key: 'recurringConsent:getConsent',
        })
    }

    function getRecurringConsent(options: GetRecurringConsentOptions = {}) {
        return useQuery(async ({ signal }) => {
            const { data } = await consentClient.GET('/public/consent/recurring', {
                signal,
                params: {
                    query: {
                        forceShow: forceShow.value ? true : undefined
                    }
                }
            })

            return data
        }, {
            ...options,
            key: 'recurringConsent:getRecurringConsent',
        })
    }

    async function decline() {
        const body: ConsentComponents['schemas']['DeclineRecurringConsentModalRequest'] = {}

        if (consentCode.value) {
            body.consent_code = consentCode.value.toString() as string | undefined
        }

        try {
            const { data, error } = await consentClient.POST('/public/consent/decline-recurring-modal', {
                body
            })

            if (error) throw error

            // Reset consentCode
            consentCode.value = undefined

            return data
        }
        catch (error) {
            return error
        }
    }

    async function setConsentStatus(consentData: ConsentComponents['schemas']['PublicPlayerConsentObject']) {
        const body: ConsentComponents['schemas']['PublicPlayerConsentObject'] = {
            ...consentData,
            brand: site.brand as unknown as ConsentRequestBrand
        }

        if (consentCode.value) {
            body.consent_code = consentCode.value.toString() as string | undefined
        }

        try {
            const { data, error } = await consentClient.PUT('/public/consent', {
                body
            })

            if (error) throw error

            // Reset consentCode
            consentCode.value = undefined

            return data
        }
        catch (error) {
            return error
        }
    }

    return {
        consentCode,
        consentStatuses,
        hasSubmittedConsent,

        hasFetchedConsents,
        isReady,
        missingEmailConsent,
        missingPhoneConsent,
        missingBingoEmailConsent,
        missingCasinoEmailConsent,
        missingBingoPhoneConsent,
        missingCasinoPhoneConsent,
        isBothConsentsMissing,
        isAnyConsentMissing,
        isAnyVerticalConsentMissing,
        showModal,
        consentTypeToFetch,
        forceShow,

        isConsentAccepted,
        getVerticalConsentType,
        getVerticalConsentKey,
        mergeTextWithSpace,
        getMergedConsentTexts,

        getConsent,
        getRecurringConsent,
        decline,
        setConsentStatus,
    }
}
